// Vendor Libs
import React from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {difference, get, filter, includes, map, noop} from 'lodash';

// Containers
import withPermissions from './with-permissions';

// Redux Actions
import {setFieldValue} from '../actions/form-data-actions';
import {globalResponseAdd, globalResponseReset} from '../actions/global-response-actions';
import {
    massActionAddTargets,
    massActionClearTargets,
    massActionRemoveTargets,
    massActionSetActive,
    massActionSetChildFormData,
    massActionSetLastId,
    massActionSetSelectAll,
    massActionSetTargets,
    massActionSubmit,
    massActionCreateAsyncMediaItemReport,
    massActionCreateAsyncMediaItemDuplicateTitlesReport,
} from '../actions/mass-action-data-actions';

// Contexts
import {FormContext} from '../contexts/form-context';
import {TabContext} from '../contexts/tab-context';

function MassAction({
    children,
    formData,
    globalResponseAdd,
    globalResponseReset,
    hasAccessByService,
    hasLockingAccess,
    massActionAddTargets,
    massActionClearTargets,
    massActionData,
    massActionName,
    massActionRemoveTargets,
    massActionSetActive,
    massActionSetChildFormData: massActionSetChildFormDataProp,
    massActionSetLastId,
    massActionSetSelectAll: massActionSetSelectAllProp,
    massActionSetTargets,
    massActionSubmit,
    massActionCreateAsyncMediaItemReport,
    massActionCreateAsyncMediaItemDuplicateTitlesReport,
    recordLocking,
    servicesSearchData,
    setFieldValue: setFieldValueProp,
    username,
}) {
    const setFieldValue = (fieldName, value) => {
        const searchData = get(servicesSearchData, `${massActionName}.searchData.content`, []);

        if (fieldName === 'targets') {
            return massActionSetTargets(
                massActionName,
                filter(searchData, (item) => includes(value, item.guid))
            );
        }

        return setFieldValueProp(`${massActionName}MassActions`, fieldName, value);
    };

    const massActionSetChildFormData = (childFormData = {}, {isTabActive, selectTab}) => {
        const {childFormName} = childFormData;

        if (
            isTabActive('MOVIESANDPROGRAMSRELATED') &&
            (childFormName === 'bulkMapShows' || childFormName === 'bulkMapSpecials')
        ) {
            selectTab('BESTMATCHES');
        }

        massActionSetChildFormDataProp(childFormData);
    };

    const hasPermission = ({guid, service: {serviceName} = {}}) =>
        (!serviceName ||
            hasAccessByService({
                accessRequirement: 'write',
                requiresServiceAccess: true,
                serviceName,
            })) &&
        (!guid || hasLockingAccess(guid));

    const filterDataLockedByCurrentUser = (massActionName, dataArray) => {
        const {lockedRecords, locallyLocked} = recordLocking;
        const dataLockedByOtherUsers = difference(
            map(get(lockedRecords, massActionName, []), 'recordId'),
            get(locallyLocked, massActionName, [])
        );
        return filter(dataArray, (data) => !includes(dataLockedByOtherUsers, data.guid));
    };

    const massActionSetSelectAll = (masActionName, selectAll) => {
        if (selectAll) {
            const searchData = get(servicesSearchData, `${massActionName}.searchData.content`, []);
            const nonLockableItems = map(
                filter(searchData, (record) => !hasPermission(record)),
                'guid'
            );

            if (nonLockableItems.length) {
                globalResponseAdd({
                    error: `Unable to lock ${nonLockableItems.length} ${
                        nonLockableItems.length === 1 ? 'record' : 'records'
                    }: ${nonLockableItems.join(', ')}`,
                });
            }

            massActionSetTargets(
                massActionName,
                filterDataLockedByCurrentUser(
                    masActionName,
                    filter(searchData, (record) => hasPermission(record))
                ),
            );
        } else {
            globalResponseReset();
        }

        massActionSetSelectAllProp(massActionName, selectAll);
    };

    const getRenderProps = (tabContext) => {
        const {
            active,
            childFormName,
            label,
            lastId,
            massActionName: name,
            selectAll,
            showMessage,
            submitted,
            targets,
        } = massActionData;
        return {
            active: active && name === massActionName,
            childFormName,
            formData,
            hasPermission,
            label,
            lastId,
            massActionAddTargets: (targets) => massActionAddTargets(massActionName, targets),
            massActionClearTargets: () => massActionClearTargets(massActionName),
            massActionRemoveTargets: (targets) => massActionRemoveTargets(massActionName, targets),
            massActionSetActive: (active) => massActionSetActive(massActionName, active),
            massActionSetChildFormData: (childFormData) => massActionSetChildFormData(childFormData, tabContext),
            massActionSetLastId,
            massActionSetSelectAll: (selectAll) => massActionSetSelectAll(massActionName, selectAll),
            massActionSetTargets: (targets) => massActionSetTargets(massActionName, targets),
            massActionSubmit: (childFormName) => massActionSubmit(massActionName, childFormName),
            massActionCreateAsyncMediaItemReport,
            massActionCreateAsyncMediaItemDuplicateTitlesReport,
            searchData: get(servicesSearchData, `${massActionName}.searchData.content`, []),
            selectAll: name === massActionName ? selectAll : false,
            showMessage,
            submitted,
            targetIds: name === massActionName ? map(targets, 'guid') : [],
            targets: name === massActionName ? targets : [],
            username,
        };
    };

    return (
        <FormContext.Provider
            value={{
                formName: `${massActionName}MassActions`,
                isFormValid: noop,
                onSubmit: noop,
                setFieldErrors: noop,
                setFieldValue,
                unregisterFieldErrors: noop,
                unregisterFieldValue: noop,
            }}
        >
            <TabContext.Consumer>
                {(tabContext) => (children ? children(getRenderProps(tabContext)) : null)}
            </TabContext.Consumer>
        </FormContext.Provider>
    );
}

MassAction.propTypes = {
    children: PropTypes.func,
    formData: PropTypes.object,
    globalResponseAdd: PropTypes.func,
    globalResponseReset: PropTypes.func,
    hasAccessByService: PropTypes.func,
    hasLockingAccess: PropTypes.func,
    massActionAddTargets: PropTypes.func,
    massActionClearTargets: PropTypes.func,
    massActionCreateAsyncMediaItemDuplicateTitlesReport: PropTypes.func,
    massActionCreateAsyncMediaItemReport: PropTypes.func,
    massActionData: PropTypes.object,
    massActionName: PropTypes.string,
    massActionRemoveTargets: PropTypes.func,
    massActionSetActive: PropTypes.func,
    massActionSetChildFormData: PropTypes.func,
    massActionSetLastId: PropTypes.func,
    massActionSetSelectAll: PropTypes.func,
    massActionSetTargets: PropTypes.func,
    massActionSubmit: PropTypes.func,
    recordLocking: PropTypes.object,
    servicesSearchData: PropTypes.object,
    setFieldValue: PropTypes.func,
    username: PropTypes.string,
};

const mapStateToProps = ({auth, formData, massActionData, recordLocking, servicesSearchData}) => ({
    massActionData,
    formData,
    recordLocking,
    servicesSearchData,
    username: auth.user,
});

const mapDispatchToProps = {
    globalResponseAdd,
    globalResponseReset,
    massActionAddTargets,
    massActionClearTargets,
    massActionRemoveTargets,
    massActionSetActive,
    massActionSetChildFormData,
    massActionSetLastId,
    massActionSetSelectAll,
    massActionSetTargets,
    massActionSubmit,
    massActionCreateAsyncMediaItemReport,
    massActionCreateAsyncMediaItemDuplicateTitlesReport,
    setFieldValue,
};

export default withPermissions(connect(mapStateToProps, mapDispatchToProps)(MassAction));
